import React, { useState } from "react";
import { graphql, navigate } from "gatsby";

import MainLayout from "../../layouts/main-layout";
import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import useUser from "../../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../../apps/utility/routes.utility";
import Link from "../atoms/link";
import checkIcon from "../../assets/icons/check.svg";
import diamondIcon from "../../assets/icons/diamond.svg";
import heartIcon from "../../assets/icons/favorite.svg";
import iphoneIcon from "../../assets/icons/iphone.svg";
import peopleIcon from "../../assets/icons/people.svg";
import starIcon from "../../assets/icons/star.svg";
import mannequinIcon from "../../assets/icons/mannequin.svg";
import worldwideIcon from "../../assets/icons/worldwide.svg";
import loveArrow from "../../assets/icons/love-arrow.svg";
import emiliaHero from "../../assets/images/about-me/hero_about_me_2.jpg.jpeg";
import heroMiniOne from "../../assets/images/about-me/hero_mini_1.jpg";
import heroMiniTwo from "../../assets/images/about-me/hero_mini_2.jpg";
import circleAboutMe from "../../assets/images/about-me/circle_about_me.png";
import emilkaSignature from "../../assets/images/emilka.png";
import logoFull from "../../assets/images/logo_full.png";
import logoFullInfill from "../../assets/images/about-me/logo_full_infill.png";
import * as styles from "./about-us.module.scss";

interface IAboutUsProps extends IPageBase {
    readonly data: {
        site: ISite;
    };
}

const stats = [
    { icon: starIcon, value: "10+", label: "lat doświadczenia w stylizacji" },
    { icon: mannequinIcon, value: "Tysiące", label: "zadowolonych klientek" },
    { icon: diamondIcon, value: "Wiedza, pasja", label: "i wyczucie trendów" },
    { icon: heartIcon, value: "Indywidualne", label: "podejście i realne efekty" },
];

const missionStats = [
    { icon: peopleIcon, value: "10 000+", label: "stylizacji tygodniowo" },
    { icon: worldwideIcon, value: "Dostępność", label: "24/7 online" },
    { icon: iphoneIcon, value: "Nowoczesne", label: "technologie" },
];

const facts = [
    "Dziennikarka modowa, specjalistka PR, stylistka i ekspertka kreowania wizerunku.",
    "Łączę modę, psychologię i kobiecą energię, pomagając kobietom odkryć ich własny styl.",
    "Inspiracji szukam podczas podróży po świecie, obserwując trendy i lokalne podejście do mody.",
    "Kocham klasykę, ale nie boję się eklektycznych połączeń, które tworzą wyjątkowy charakter stylizacji.",
    "Pasjonatka sportu, kalisteniki, jogi i akrobatyki.",
    "FashionFairy.pl stworzyłam z miłości do kobiecych metamorfoz i przekonania, że każda kobieta może wyglądać i czuć się wyjątkowo.",
];

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

const AboutUs: React.FC<IAboutUsProps> = ({ data, pageContext }) => {
    const { pageTitle, defaultStructuredData } = usePageMetadata(pageContext, data.site);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isLoggedIn } = useUser();
    const accountLinkHref = isLoggedIn ? getAbsolutePath("ACCOUNT_DASHBOARD") : "/auth/";
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";

    return (
        <MainLayout
            HeaderComponent={null}
            footer={false}
            className={styles.aboutPage}
            SEOProps={{
                title: pageTitle,
                structuredData: defaultStructuredData,
            }}
        >
            <nav className={styles.aboutNav} aria-label="Główna nawigacja">
                <Link to="/" className={styles.navBrand}>
                    <picture>
                        <source media="(min-width: 861px)" srcSet={logoFullInfill} />
                        <img src={logoFull} alt="Fashion Fairy Virtual Stylist" />
                    </picture>
                </Link>
                <div className={styles.navLinks}>
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                </div>
                <Link to={accountLinkHref} className={styles.loginLink} noPrefetch>
                    {accountLinkLabel}
                </Link>
                <button
                    className={styles.menuToggle}
                    type="button"
                    aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="about-mobile-menu"
                    onClick={() => setIsMenuOpen(isOpen => !isOpen)}
                >
                    <span />
                    <span />
                    <span />
                </button>
                <div
                    id="about-mobile-menu"
                    className={`${styles.mobileMenu} ${
                        isMenuOpen ? styles.mobileMenuOpen : ""
                    }`}
                >
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            setIsMenuOpen(false);
                            navigate(accountLinkHref);
                        }}
                    >
                        {accountLinkLabel}
                    </button>
                </div>
            </nav>

            <section className={styles.hero}>
                <img
                    src={emiliaHero}
                    alt=""
                    className={styles.heroImage}
                    aria-hidden="true"
                />
                <div className={styles.heroOverlay} />

                <div className={styles.heroContent}>
                    <h1>
                        To ja,
                        <span>Emilia</span>
                    </h1>
                    <p className={styles.subtitle}>Twoja wirtualna stylistka</p>
                    <div className={styles.introText}>
                        <p>
                            Od zawsze wierzyłam, że styl to coś więcej niż ubrania.
                            To sposób, w jaki pokazujesz światu swoją osobowość,
                            pewność siebie i marzenia.
                        </p>
                        <p>
                            Fashion Fairy powstało z pasji do mody i z potrzeby
                            dzielenia się wiedzą w nowoczesny, dostępny i inspirujący
                            sposób.
                        </p>
                        <p>
                            Łączę doświadczenie stylistki, wiedzę technologiczną i
                            kobiece podejście, by pomóc Ci odkryć swój unikalny styl
                            bez wychodzenia z domu.
                        </p>
                    </div>
                </div>

                <div className={styles.statsCard}>
                    {stats.map(item => (
                        <div key={item.value} className={styles.statItem}>
                            <span
                                className={styles.statIcon}
                                style={{
                                    WebkitMaskImage: `url(${item.icon})`,
                                    maskImage: `url(${item.icon})`,
                                }}
                                aria-hidden="true"
                            />
                            <strong>{item.value}</strong>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>

                <aside className={styles.quoteCard}>
                    <img
                        src={circleAboutMe}
                        alt="Styl to nie tylko to, co nosisz. To sposób, w jaki żyjesz."
                    />
                </aside>

                <div className={styles.signature}>
                    <img
                        src={emilkaSignature}
                        alt="Emilia - Wirtualna stylistka"
                    />
                </div>
            </section>

            <section className={styles.bottomSection}>
                <div className={styles.mission}>
                    <h2>Moja misja</h2>
                    <span
                        className={`${styles.titleSeparator} ${styles.titleSeparatorLight}`}
                        aria-hidden="true"
                    >
                        <img src={loveArrow} alt="" />
                    </span>
                    <p>
                        Pomagam kobietom poczuć się pięknie, pewnie i wyjątkowo
                        każdego dnia. Pokazuję, że stylizacja może być prosta,
                        przyjemna i dopasowana do Twojego życia, sylwetki i budżetu.
                    </p>
                    <div className={styles.missionStats}>
                        {missionStats.map(item => (
                            <div key={item.value}>
                                <span aria-hidden="true">
                                    <img src={item.icon} alt="" />
                                </span>
                                <strong>{item.value}</strong>
                                <small>{item.label}</small>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.factsCard}>
                    <div>
                        <h2>Kilka faktów o mnie</h2>
                        <span
                            className={styles.titleSeparator}
                            aria-hidden="true"
                        >
                            <img src={loveArrow} alt="" />
                        </span>
                        <ul>
                            {facts.map(item => (
                                <li key={item}>
                                    <img src={checkIcon} alt="" aria-hidden="true" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={styles.polaroids} aria-hidden="true">
                        <img src={heroMiniOne} alt="" />
                        <img src={heroMiniTwo} alt="" />
                        <span>E♡</span>
                    </div>
                </div>
            </section>
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }

        site {
            ...siteFields
        }
    }
`;

export default AboutUs;
